import {
  useCallback, useEffect, useInsertionEffect, useRef, useState,
} from 'react';

// Ported from Frontend-Platform `libs/ui/src/hooks/useControllableState/useControllableState.ts`,
// itself adapted from https://github.com/radix-ui/primitives/blob/main/packages/react/use-controllable-state/src/useControllableState.tsx

// ============================================================
//     TYPES
// ============================================================
type ChangeHandler<T> = (state: T) => void;

type SetStateFn<T> = React.Dispatch<React.SetStateAction<T>>;

interface UseControllableStateParams<T> {
  defaultProp: T;
  onChange?: ChangeHandler<T>;
  prop: T | undefined;
}

const NoOp = () => {};

// ============================================================
//     HELPERS
// ============================================================
const useUncontrolledState = <T>({
  defaultProp,
  onChange,
}: Omit<UseControllableStateParams<T>, 'prop'>): [
  value: T,
  setValue: React.Dispatch<React.SetStateAction<T>>,
  onChangeRef: React.MutableRefObject<ChangeHandler<T> | undefined>,
  ] => {
  const [value, setValue] = useState(defaultProp);
  const prevValueRef = useRef(value);
  const onChangeRef = useRef(onChange);

  // `useInsertionEffect` is just a super fancy `useEffect` with much higher priority. It'll always run before all other useEffects.
  useInsertionEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (prevValueRef.current !== value) {
      onChangeRef.current?.(value);
      prevValueRef.current = value;
    }
  }, [value, prevValueRef]);

  return [value, setValue, onChangeRef];
};

// ============================================================
//     EXPORT
// ============================================================
/**
 * A hook to manage internal state for a component that can be both controlled and uncontrolled.
 *
 * @example
 * ```tsx
 * const Component = ({ defaultValue, value, onChange, ...props}) => {
 *   const [val, setVal] = useControllableState({
 *     defaultProp: defaultValue,
 *     onChange,
 *     prop: value,
 *   });
 *
 *   return (
 *     <div {...props}>
 *       <input value={val} onChange={setVal} />
 *     </div>
 *   );
 * }
 * ```
 */
export const useControllableState = <T>({
  defaultProp,
  onChange = NoOp,
  prop,
}: UseControllableStateParams<T>): [T, SetStateFn<T>] => {
  const [uncontrolledProp, setUncontrolledProp, onChangeRef] = useUncontrolledState({
    defaultProp,
    onChange,
  });
  const isControlled = prop !== undefined;
  const value = isControlled ? prop : uncontrolledProp;

  const setValue = useCallback<SetStateFn<T>>(
    nextValue => {
      if (isControlled) {
        const val = nextValue instanceof Function ? nextValue(prop) : nextValue;
        if (val !== prop) {
          onChangeRef.current?.(val);
        }
      } else {
        setUncontrolledProp(nextValue);
      }
    },
    [isControlled, prop, setUncontrolledProp, onChangeRef],
  );

  return [value, setValue];
};
